import React, { useEffect, memo, Fragment, useState, useRef } from "react";
import { Card, Dropdown, Row, Col, Button, Modal } from 'react-bootstrap';
import { Link, useNavigate } from "react-router-dom";
import ApexCharts from "react-apexcharts";
import SectionLoader from "../../components/SectionLoader";

// AOS
import AOS from "aos";
import "../../../node_modules/aos/dist/aos";
import "../../../node_modules/aos/dist/aos.css";

// Count-up
import CountUp from "react-countup";

// Redux Selector / Action
import { useSelector } from "react-redux";
import * as SettingSelector from "../../store/setting/selectors";
import { clearAuthStorage } from "../../interceptor";

const Index = memo((props) => {
  let history = useNavigate();
  useSelector(SettingSelector.theme_color);

  let token = sessionStorage.getItem("revizeTrainingToken");
  const userRole = sessionStorage.getItem("userRole");
  const statsApi = `${process.env.REACT_APP_SERVER_URL}/api/stats`;
  const [siteCount, setSiteCount] = useState(0);
  const [trainedDocCount, setTrainedDocCount] = useState(0);
  const [userCount, setUserCount] = useState(0);
  const [fstData, setFSTData] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [siteName, setSiteName] = useState("");
  const [siteKey, setSiteKey] = useState("");
  const [loadingStats, setLoadingStats] = useState(true);
  const userApi = `${process.env.REACT_APP_SERVER_URL}/api/users/me`;

  // Refs to track API calls - RESET when filters change
  const apiCallTracker = useRef({
    stats: false,
    user: false,
    websites: false,
    wordCount: {},
    promptCount: {},
    topPrompts: {},
    userAnalytics: {},
    reactionCount: {}
  });

  // Set page background color
  useEffect(() => {
    document.body.style.backgroundColor = '#EFF5FB';

    // Cleanup function to reset background color when component unmounts
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  useEffect(() => {
    if (!token) {
      history('/auth/sign-in');
      return;
    }

    // Prevent duplicate API calls
    if (apiCallTracker.current.stats) return;
    apiCallTracker.current.stats = true;

    const requestOptions = {
      method: "GET",
      headers: { "accept": "application/json", "Authorization": "Bearer " + token },
    };

    fetch(statsApi, requestOptions)
      .then(function (response) {
        return response.json();
      }).then(function (data) {
        if (!data) {
          return;
        }
        if (userRole === "ADMIN" || userRole === "SITEADMIN") {
          setUserCount(data['user_count']);
        }
        if (userRole !== "ADMIN") {
          setSiteId(data['site_id']);
          setSiteName(data['site_name']);
          setSiteKey(data['site_key']);
        }
        setSiteCount(data['site_count']);
        setTrainedDocCount(data['trained_doc_count']);
        setFSTData(data['fst_requests'])
      }).catch(err => console.log("error", err))
      .finally(() => setLoadingStats(false));

  }, [statsApi, history, token, userRole]);

  useEffect(() => {
    if (!token) {
      history('/auth/sign-in');
      return;
    }

    // Prevent duplicate API calls
    if (apiCallTracker.current.user) return;
    apiCallTracker.current.user = true;

    const requestOptions = {
      method: "GET",
      headers: { "accept": "application/json", "Authorization": "Bearer " + token },
    };

    fetch(userApi, requestOptions)
      .then(function (response) {
        return response.json();
      }).then(function (data) {
        sessionStorage.setItem("loggedInUserId", data.id);
        if (data.is_active === false) {
          clearAuthStorage();
          window.location.href = '/auth/sign-in';
        }
      }).catch(err => {
        console.log("error", err);
        clearAuthStorage();
        window.location.href = '/auth/sign-in';
      });
  }, [token, history, userApi]);

  const getVariableColor = () => {
    let prefix =
      getComputedStyle(document.body).getPropertyValue("--prefix") || "bs-";
    if (prefix) {
      prefix = prefix.trim();
    }
    const color1 = getComputedStyle(document.body).getPropertyValue(
      `--${prefix}primary`
    );
    const color2 = getComputedStyle(document.body).getPropertyValue(
      `--${prefix}info`
    );
    const color3 = getComputedStyle(document.body).getPropertyValue(
      `--${prefix}primary-tint-20`
    );
    const color4 = getComputedStyle(document.body).getPropertyValue(
      `--${prefix}warning`
    );
    return {
      primary: color1.trim(),
      info: color2.trim(),
      warning: color4.trim(),
      primary_light: color3.trim(),
    };
  };
  const variableColors = getVariableColor();

  const colors = [variableColors.primary, variableColors.info];
  useEffect(() => {
    return () => colors;
  });

  useEffect(() => {
    AOS.init({
      startEvent: "DOMContentLoaded",
      disable: function () {
        var maxWidth = 996;
        return window.innerWidth < maxWidth;
      },
      throttleDelay: 10,
      once: true,
      duration: 700,
      offset: 10,
    });
  });

  const [timePeriod, setTimePeriod] = useState('Daily');
  const [websiteNameList, setWebsiteNameList] = useState([]);
  const [websiteNameListwithIds, setWebsiteNameListWIthIds] = useState({ "All": "All" });
  const [selectedWebsite, setSelectedWebsite] = useState("All");
  const user_id = sessionStorage.getItem("loggedInUserId");

  useEffect(() => {
    if (!user_id) return;

    // Prevent duplicate API calls
    if (apiCallTracker.current.websites) return;
    apiCallTracker.current.websites = true;

    const fetchWebsites = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/analytics/website_list?user_id=${user_id}`);
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        const names = [];
        const nameIdMap = { "All": "All" };
        for (const record of data) {
          names.push(record['name']);
          nameIdMap[record['name']] = record['id'];
        }
        setWebsiteNameList(names);
        setWebsiteNameListWIthIds(nameIdMap);
      } catch (error) {
        console.error("Error fetching website list:", error);
      }
    };

    fetchWebsites();
  }, [user_id]);

  const [graphData, setGraphData] = useState({ date: [], query_word_count: [], response_word_count: [] });
  const [loadingWordCount, setLoadingWordCount] = useState(true);

  useEffect(() => {
    if (!user_id) {
      setLoadingWordCount(false);
      return;
    }

    const key = `wordCount_${user_id}_${websiteNameListwithIds[selectedWebsite]}_${timePeriod}`;
    
    // RESET the tracker when dependencies change to allow fresh API calls
    apiCallTracker.current.wordCount[key] = false;

    if (apiCallTracker.current.wordCount[key]) return;
    apiCallTracker.current.wordCount[key] = true;

    const fetchWebsiteWordCount = async () => {
      setLoadingWordCount(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/api/analytics/website_word_count?user_id=${user_id}&website=${websiteNameListwithIds[selectedWebsite]}&timePeriod=${timePeriod}`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        setGraphData({
          date: data && data.date ? data.date : [],
          query_word_count: data && data.query_word_count ? data.query_word_count : [],
          response_word_count: data && data.response_word_count ? data.response_word_count : [],
        });
      } catch (error) {
        console.error('Error fetching website word count:', error);
      } finally {
        setLoadingWordCount(false);
      }
    };

    fetchWebsiteWordCount();
  }, [selectedWebsite, timePeriod, user_id, websiteNameListwithIds]);

  const chartOptions = {
  chart: {
    type: "line",
    zoom: { enabled: false },
  },
  xaxis: {
    categories: graphData.date || [],
    labels: {
      show: true,
      rotate: -45,
      style: { fontSize: "11px" },
      hideOverlappingLabels: false,
      showDuplicates: true,

      // 1. LABELS – show only every 3rd date when Daily
      formatter: (value, opts) => {
        const idx = opts?.dataPointIndex ?? 0;
        if (timePeriod === "Daily") {
          return idx % 3 === 0 ? value : "\u200B";   // zero-width space = hidden
        }
        return value;
      },
    },

    tickAmount:
      timePeriod === "Daily" && graphData?.date?.length
        ? Math.min(Math.ceil(graphData.date.length / 3), 12)
        : undefined,
    tickPlacement: "on",
    title: {
      text: "Dates",
      style: { fontSize: "12px", color: "#333" },
      offsetY: -10,
    },
  },

  yaxis: {
    title: { text: "Word Count" },
  },

  dataLabels: { enabled: false },
  stroke: { curve: "smooth" },

  tooltip: {
    enabled: true,

    // 2. TOOLTIP – always show the real date (even hidden labels)
    x: {
      formatter: (value, opts) => {
        const idx = opts?.dataPointIndex ?? 0;
        return graphData.date?.[idx] ?? value;
      },
    },

    shared: true,
    intersect: false,
  },

  colors: ["#E6E8EC", "#5A6ACF"],
  legend: {
    position: "bottom",
    horizontalAlign: "center",
    offsetY: 4,
  },
};

  const chartSeries = [
    {
      name: "Query Word Count",
      data: graphData.query_word_count || [],
    },
    {
      name: "Response Word Count",
      data: graphData.response_word_count || [],
    },
  ];

  const [selectedWebsiteForPromptCount, setSelectedWebsiteForPromptCount] = useState("All");
  const [dateFilterForPromptCount, setDateFilterForPromptCount] = useState("Daily");
  const [chartDataForPromptCount, setChartDataForPromptCount] = useState({ date: [], total_count: [] });
  const [loadingPromptCount, setLoadingPromptCount] = useState(true);

  useEffect(() => {
    if (!user_id) {
      setLoadingPromptCount(false);
      return;
    }

    const key = `promptCount_${user_id}_${websiteNameListwithIds[selectedWebsiteForPromptCount]}_${dateFilterForPromptCount}`;
    
    // RESET the tracker when dependencies change to allow fresh API calls
    apiCallTracker.current.promptCount[key] = false;

    if (apiCallTracker.current.promptCount[key]) return;
    apiCallTracker.current.promptCount[key] = true;

    const fetchPromptCount = async () => {
      setLoadingPromptCount(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/api/analytics/website_prompt_count?user_id=${user_id}&website=${websiteNameListwithIds[selectedWebsiteForPromptCount]}&timePeriod=${dateFilterForPromptCount}`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        setChartDataForPromptCount({
          date: data && data.date ? data.date : [],
          total_count: data && data.total_count ? data.total_count : [],
        });
      } catch (error) {
        console.error("Error fetching prompt count:", error);
      } finally {
        setLoadingPromptCount(false);
      }
    };

    fetchPromptCount();
  }, [selectedWebsiteForPromptCount, dateFilterForPromptCount, user_id, websiteNameListwithIds]);

  const chartOptionsForPromptCount = {
  chart: {
    type: "bar",
    zoom: { enabled: false },
  },
  xaxis: {
    categories: chartDataForPromptCount?.date || [],
    labels: {
      show: true,
      rotate: -45,
      style: { fontSize: "11px" },
      hideOverlappingLabels: false,
      showDuplicates: true,

      // 1. LABELS – show only every 3rd date when Daily
      formatter: (value, opts) => {
        const idx = opts?.dataPointIndex ?? 0;
        if (dateFilterForPromptCount === "Daily") {
          return idx % 3 === 0 ? value : "\u200B";
        }
        return value;
      },
    },

    tickAmount:
      dateFilterForPromptCount === "Daily" &&
      chartDataForPromptCount?.date?.length
        ? Math.min(Math.ceil(chartDataForPromptCount.date.length / 3), 12)
        : undefined,
    tickPlacement: "on",

    title: {
      text: "Dates",
      style: { fontSize: "12px", color: "#333" },
      offsetY: -10,
    },
  },

  yaxis: {
    title: { text: "Prompt Count" },
  },

  dataLabels: { enabled: false },
  stroke: { curve: "smooth" },

  tooltip: {
    enabled: true,
    // 2. TOOLTIP – always show full date
    x: {
      formatter: (value, opts) => {
        const idx = opts?.dataPointIndex ?? 0;
        return chartDataForPromptCount?.date?.[idx] ?? value;
      },
    },
    shared: true,
    intersect: false,
  },

  colors: ["#5A6ACF"],
  legend: {
    show: true,
    position: "bottom",
    horizontalAlign: "center",
  },
};


  const chartSeriesForPromptCount = [
    {
      name: "Prompt Count",
      data: chartDataForPromptCount?.total_count || [],
    },
  ];

  const [selectedWebsiteForTopSearchedPrompts, setSelectedWebsiteForTopSearchedPrompts] = useState("Select Website");
  const [selectedTopPromptLimit, setSelectedTopPromptLimit] = useState(5);
  const [topSearchedPrompts, setTopSearchedPrompts] = useState({ prompts: [], last_updated: null });
  const [fetchingPrompts, setFetchingPrompts] = useState(false);

  useEffect(() => {
    const key = `topPrompts_${websiteNameListwithIds[selectedWebsiteForTopSearchedPrompts]}_${selectedTopPromptLimit}`;
    
    // RESET the tracker when dependencies change to allow fresh API calls
    apiCallTracker.current.topPrompts[key] = false;

    if (apiCallTracker.current.topPrompts[key]) return;
    apiCallTracker.current.topPrompts[key] = true;

    const fetchTopSearchedPrompts = async () => {
      try {
        if (selectedWebsiteForTopSearchedPrompts === "Select Website") {
          setTopSearchedPrompts({ prompts: [], last_updated: null });
          return;
        }
        setFetchingPrompts(true);
        const response = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/api/analytics/get_most_searched_unique_prompts?website=${websiteNameListwithIds[selectedWebsiteForTopSearchedPrompts]}&limit=${selectedTopPromptLimit}`
        );
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        
        let prompts = [];
        let lastUpdated = null;
        
        if (Array.isArray(data)) {
          if (data.length === 2 && Array.isArray(data[0]) && (typeof data[1] === 'string' || data[1] instanceof String)) {
            prompts = data[0];
            lastUpdated = data[1];
          } else {
            prompts = data;
          }
        } else if (data && typeof data === 'object') {
          prompts = data.prompts || data.data || [];
          lastUpdated = data.last_updated || data.timestamp || null;
        }

        setTopSearchedPrompts({ 
          prompts: prompts, 
          last_updated: lastUpdated 
        });
        
      } catch (error) {
        console.error("Error fetching top searched prompts:", error);
        setTopSearchedPrompts({ prompts: [], last_updated: null });
      } finally {
        setFetchingPrompts(false);
      }
    };

    fetchTopSearchedPrompts();
  }, [selectedWebsiteForTopSearchedPrompts, websiteNameListwithIds, selectedTopPromptLimit]);

  const [regionalAnalyticsChartOptions, setRegionalAnalyticsChartOptions] = useState({
    chart: {
      type: "bar",
      zoom: { enabled: false },
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
    },
    xaxis: { categories: [] },
  });
  const [regionalAnalyticsChartSeries, setRegionalAnalyticsChartSeries] = useState([
    { name: "Total Active Users", data: [] },
  ]);
  const [dateFilterForRegionalAnalytics, setDateFilterForRegionalAnalytics] = useState("Daily");
  const [selectedWebsiteForUserAnalytics, setSelectedWebsiteForUserAnalytics] = useState("All");

  // State for modal
  const [showModal, setShowModal] = useState(false);
  const [selectedDateData, setSelectedDateData] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');

  // State to store raw analytics data for modal
  const [, setUserAnalyticsRawData] = useState([]);
  const [loadingUserAnalytics, setLoadingUserAnalytics] = useState(true);

  useEffect(() => {
    if (!user_id) {
      setLoadingUserAnalytics(false);
      return;
    }

    const key = `userAnalytics_${user_id}_${websiteNameListwithIds[selectedWebsiteForUserAnalytics]}_${dateFilterForRegionalAnalytics}`;
    
    // RESET the tracker when dependencies change to allow fresh API calls
    apiCallTracker.current.userAnalytics[key] = false;

    if (apiCallTracker.current.userAnalytics[key]) return;
    apiCallTracker.current.userAnalytics[key] = true;

    const fetchAnalyticsData = async () => {
      setLoadingUserAnalytics(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/api/analytics/get_user_analytics?user_id=${user_id}&website=${websiteNameListwithIds[selectedWebsiteForUserAnalytics]}&timePeriod=${dateFilterForRegionalAnalytics}`
        );
        const data = await response.json();
        const rows = Array.isArray(data) ? data : [];

        // Calculate total users per date (non-stacked)
        const dates = rows.map((item) => item.date);
        const totals = rows.map((item) =>
          (item.data || []).reduce((sum, entry) => sum + entry.activeUsers, 0)
        );

        // Store the raw data for modal use
        setUserAnalyticsRawData(rows);

        setRegionalAnalyticsChartOptions({
          chart: {
            type: "bar",
            zoom: { enabled: false },
            toolbar: {
              show: true,
              tools: {
                download: true,
                selection: false,
                zoom: false,
                zoomin: false,
                zoomout: false,
                pan: false,
                reset: false,
              },
            },
            events: {
              dataPointSelection: (event, chartContext, config) => {
                const selectedDate = dates[config.dataPointIndex];
                const dateData = rows.find(item => item.date === selectedDate);
                if (dateData) {
                  setSelectedDateData(dateData.data || []);
                  setSelectedDate(selectedDate);
                  setShowModal(true);
                }
              }
            }
          },
          xaxis: {
            categories: dates,
            labels: {
              show: true,
              rotate: -45,
              style: { fontSize: "11px" },
              hideOverlappingLabels: false,
              showDuplicates: true,
              // Add the same label formatting logic as other charts
              formatter: (value, opts) => {
                const idx = opts?.dataPointIndex ?? 0;
                if (dateFilterForRegionalAnalytics === "Daily") {
                  return idx % 3 === 0 ? value : "\u200B"; // zero-width space for hidden labels
                }
                return value;
              },
            },
            // Add the same tick amount logic as other charts
            tickAmount:
              dateFilterForRegionalAnalytics === "Daily" && dates.length
                ? Math.min(Math.ceil(dates.length / 3), 12)
                : undefined,
            tickPlacement: "on",
            title: { 
              text: "Dates",
              style: { fontSize: "12px", color: "#333" },
              offsetY: -10,
            },
          },
          yaxis: {
            title: { text: "Active Users" },
          },
          // Add tooltip configuration like other charts
          tooltip: {
            enabled: true,
            x: {
              formatter: (value, opts) => {
                const idx = opts?.dataPointIndex ?? 0;
                return dates[idx] ?? value;
              },
            },
            shared: true,
            intersect: false,
          },
          colors: ["#5A6ACF"],
          plotOptions: {
            bar: {
              columnWidth: '60%',
            }
          },
          legend: {
            position: "bottom",
            horizontalAlign: "center"
          }
        });

        setRegionalAnalyticsChartSeries([{
          name: "Total Active Users",
          data: totals
        }]);
      } catch (error) {
        console.error("Error fetching analytics data:", error);
      } finally {
        setLoadingUserAnalytics(false);
      }
    };

    fetchAnalyticsData();
  }, [selectedWebsiteForUserAnalytics, dateFilterForRegionalAnalytics, user_id, websiteNameListwithIds]);

  const [selectedWebsiteForReactionCount, setSelectedWebsiteForReactionCount] = useState("All");
  const [dateFilterForReactionCount, setDateFilterForReactionCount] = useState("Daily");
  const [reactionCountData, setReactionCountData] = useState({
    date: [],
    positive: [],
    neutral: [],
    negative: []
  });
  const [loadingReactionCount, setLoadingReactionCount] = useState(true);

  useEffect(() => {
    if (!user_id) {
      setLoadingReactionCount(false);
      return;
    }

    const key = `reactionCount_${user_id}_${websiteNameListwithIds[selectedWebsiteForReactionCount]}_${dateFilterForReactionCount}`;
    
    // RESET the tracker when dependencies change to allow fresh API calls
    apiCallTracker.current.reactionCount[key] = false;

    if (apiCallTracker.current.reactionCount[key]) return;
    apiCallTracker.current.reactionCount[key] = true;

    const fetchReactionCount = async () => {
      setLoadingReactionCount(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/api/analytics/website_reaction_count?user_id=${user_id}&website=${websiteNameListwithIds[selectedWebsiteForReactionCount]}&timePeriod=${dateFilterForReactionCount}`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        setReactionCountData({
          date: data && data.date ? data.date : [],
          positive: data && data.positive ? data.positive : [],
          neutral: data && data.neutral ? data.neutral : [],
          negative: data && data.negative ? data.negative : [],
        });
      } catch (error) {
        console.error("Error fetching reaction count:", error);
      } finally {
        setLoadingReactionCount(false);
      }
    };

    fetchReactionCount();
  }, [selectedWebsiteForReactionCount, dateFilterForReactionCount, user_id, websiteNameListwithIds]);

  const reactionChartOptions = {
  chart: {
    type: "line",
    zoom: { enabled: false },
  },
  xaxis: {
    categories: reactionCountData.date || [],
    labels: {
      show: true,
      rotate: -45,
      style: { fontSize: "11px" },
      hideOverlappingLabels: false,
      showDuplicates: true,

      // 1. LABELS – show only every 3rd date when Daily
      formatter: (value, opts) => {
        const idx = opts?.dataPointIndex ?? 0;
        if (dateFilterForReactionCount === "Daily") {
          return idx % 3 === 0 ? value : "\u200B";
        }
        return value;
      },
    },

    tickAmount:
      dateFilterForReactionCount === "Daily" &&
      reactionCountData?.date?.length
        ? Math.min(Math.ceil(reactionCountData.date.length / 3), 12)
        : undefined,
    tickPlacement: "on",

    title: {
      text: "Dates",
      style: { fontSize: "12px", color: "#333" },
      offsetY: -10,
    },
  },

  yaxis: {
    title: { text: "Reaction Count" },
  },

  stroke: {
    curve: "smooth",
    width: 4,
  },

  tooltip: {
    enabled: true,
    // 2. TOOLTIP – always show full date
    x: {
      formatter: (value, opts) => {
        const idx = opts?.dataPointIndex ?? 0;
        return reactionCountData?.date?.[idx] ?? value;
      },
    },
    shared: true,
    intersect: false,
  },

  colors: ["#5A6ACF", "#E6E8EC", "#FF4560"],
  legend: {
    position: "bottom",
    horizontalAlign: "center",
    offsetY: 5,
  },
};


  const reactionChartSeries = [
    {
      name: "Positive Reactions",
      data: reactionCountData.positive || [],
    },
    {
      name: "Neutral Reactions",
      data: reactionCountData.neutral || [],
    },
    {
      name: "Negative Reactions",
      data: reactionCountData.negative || [],
    },
  ];

  // Custom styles for dropdowns with #5A6ACF color
  const customDropdownStyles = {
    dropdownToggle: {
      backgroundColor: 'white',
      borderColor: '#5A6ACF',
      color: '#5A6ACF'
    },
    dropdownMenu: {
      borderColor: '#5A6ACF'
    },
    dropdownItem: {
      active: {
        backgroundColor: '#5A6ACF',
        color: 'white'
      },
      hover: {
        backgroundColor: '#f8f9fa'
      }
    }
  };

  // Format the last updated timestamp
  const formatLastUpdated = (timestamp) => {
    if (!timestamp) return "Last updated: Unknown";
    
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        return "Last updated: Invalid date";
      }
      
      const now = new Date();
      const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
      
      if (diffInHours < 1) {
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));
        return `Last updated ${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
      } else if (diffInHours < 24) {
        return `Last updated ${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
      } else {
        return `Last updated at ${date.toLocaleDateString()} ${date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`;
      }
    } catch (error) {
      console.error("Error formatting timestamp:", error);
      return "Last updated: Unknown";
    }
  };

  return (
    <Fragment>
      {token !== undefined && (
        <div className="dashboard-container" style={{ backgroundColor: '#EFF5FB', minHeight: '100vh', padding: '20px 0' }}>
          <style>
            {`
              /* Mobile responsive styles for dashboard */
              @media (max-width: 768px) {
                .dashboard-container {
                  padding: 10px 0 !important;
                }
                
                .stats-container {
                  flex-direction: column !important;
                  width: 100% !important;
                  padding: 15px !important;
                  margin: 10px 0 !important;
                }
                
                .stats-container > * {
                  width: 100% !important;
                  padding: 15px 10px !important;
                  margin-bottom: 10px !important;
                  border-bottom: 1px solid #eee;
                }
                
                .stats-container > *:not(:last-child)::after {
                  display: none !important;
                }
                
                .stats-container > *:last-child {
                  border-bottom: none;
                  margin-bottom: 0;
                }
                
                .icon {
                  width: 60px !important;
                  height: 60px !important;
                  font-size: 24px !important;
                }
                
                .stat-value {
                  font-size: 24px !important;
                }
                
                .stat-label {
                  font-size: 13px !important;
                }
                
                /* Chart container adjustments */
                .dashboard-section {
                  padding: 15px !important;
                  margin: 10px 0 !important;
                  width: 100% !important;
                  border-radius: 15px !important;
                }
                
                .charts-flex-container {
                  background: transparent !important;
                }
                
                .charts-flex-row {
                  flex-direction: column !important;
                  min-width: unset !important;
                }
                
                .chart-card {
                  padding: 15px !important;
                  margin-bottom: 15px;
                  border-radius: 12px;
                  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                }
                
                .chart-card:not(:last-child)::after {
                  display: none !important;
                }
                
                .top-searched-prompts-card {
                  width: 100% !important;
                  margin: 10px 0 !important;
                  padding: 15px !important;
                }
                
                /* Dropdown adjustments */
                .custom-dropdown .dropdown-toggle {
                  font-size: 12px;
                  padding: 4px 8px;
                }
                
                .dropdown-menu {
                  font-size: 12px;
                }
                
                /* Chart title adjustments */
                h5 {
                  font-size: 16px;
                }
              }
              
              @media (max-width: 576px) {
                .stats-container {
                  padding: 10px !important;
                }
                
                .icon {
                  width: 50px !important;
                  height: 50px !important;
                  font-size: 20px !important;
                }
                
                .stat-value {
                  font-size: 20px !important;
                }
                
                .chart-card {
                  padding: 10px !important;
                }
                
                .dropdown-menu {
                  max-width: 200px;
                }
              }
              
              /* Tablet responsive styles */
              @media (min-width: 769px) and (max-width: 1024px) {
                .stats-container {
                  padding: 20px 15px !important;
                  width: calc(100% - 30px) !important;
                }
                
                .dashboard-section {
                  padding: 20px !important;
                  margin: 15px 20px !important;
                  width: calc(100% - 40px) !important;
                }
                
                .charts-flex-row {
                  min-width: unset !important;
                }
                
                .chart-card {
                  padding: 15px !important;
                }
                
                .top-searched-prompts-card {
                  width: calc(100% - 40px) !important;
                  margin: 15px 20px !important;
                }
              }

              /* Zoom responsive styles */
              @media (min-width: 1025px) {
                .dashboard-section {
                  max-width: 100%;
                  overflow: hidden;
                }
                
                .charts-flex-container {
                  max-width: 100%;
                  overflow: hidden;
                }
                
                .charts-flex-row {
                  min-width: unset;
                  max-width: 100%;
                }
                
                .chart-card {
                  overflow: hidden;
                }
                
                .apexcharts-canvas {
                  max-width: 100% !important;
                  overflow: hidden;
                }
                
                .apexcharts-inner {
                  transform-origin: center center;
                }
              }

              /* Existing dashboard styles */
              .dashboard-container .stats-container {
                display: flex;
                justify-content: space-around;
                align-items: center;
                background: #fafafa;
                padding: 25px 10px 25px 30px;
                border-radius: 20px;
                box-shadow: 0 2px 6px rgba(0,0,0,0.1);
                margin: 20px auto;
                width: calc(100% - 55px); 
                min-height: 150px;
                max-width: 100%;
                overflow: hidden;
              }
              .dashboard-container .stats-container > * {
                flex: 1;
                display: flex;
                align-items: center;
                gap: 15px;
                padding: 10px;
                position: relative;
                text-decoration: none;
                color: inherit;
                max-width: 100%;
                overflow: hidden;
              }
              .dashboard-container .stats-container > *:not(:last-child)::after {
                content: "";
                position: absolute;
                top: 15%;
                right: 0;
                height: 70%;
                width: 1px;
                background: #ddd;
              }
              .dashboard-container .stats-container > *:hover {
                text-decoration: none;
                color: inherit;
              }
              .dashboard-container .icon {
                width: 80px;
                height: 80px;
                border-radius: 50%;
                background: #e8fff1;
                display: flex;
                justify-content: center;
                align-items: center;
                color: #00c853;
                font-size: 32px;
                flex-shrink: 0;
              }
              .dashboard-container .stat-info {
                display: flex;
                flex-direction: column;
                max-width: calc(100% - 95px);
                overflow: hidden;
              }
              .dashboard-container .stat-label {
                font-size: 14px;
                color: #ACACAF;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
              .dashboard-container .stat-value {
                font-size: 32px;
                font-weight: 600;
                color: #212529;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
              .dashboard-container .growth {
                font-size: 12px;
                color: #00c853;
                margin-top: 4px;
              }
              
              /* Top Searched Prompts Card - Same height as stats container */
              .dashboard-container .top-searched-prompts-card {
                min-height: 150px;
                display: flex;
                flex-direction: column;
                max-width: 100%;
                overflow: visible;
              }
              .dashboard-container .top-searched-prompts-card .card-body {
                flex: 1;
                display: flex;
                flex-direction: column;
                max-width: 100%;
                overflow: visible;
              }
              .dashboard-container .top-searched-prompts-content {
                flex: 1;
                display: flex;
                flex-direction: column;
                justify-content: center;
                max-width: 100%;
                overflow: visible;
              }

              /* Flex container for charts with separation lines */
              .dashboard-container .charts-flex-container {
                display: flex;
                flex-direction: column;
                width: 100%;
                background-color: #ddd;
                max-width: 100%;
                overflow: hidden;
              }
              .dashboard-container .charts-flex-row {
                display: flex;
                width: 100%;
                max-width: 100%;
                overflow: hidden;
              }
              .dashboard-container .chart-card {
                flex: 1;
                background: white;
                padding: 20px;
                min-width: 0;
                position: relative;
                max-width: 100%;
                overflow: hidden;
              }
              /* Vertical separation lines */
              .dashboard-container .chart-card:not(:last-child)::after {
                content: "";
                position: absolute;
                top: 0;
                right: 0;
                height: 100%;
                width: 1px;
                background: #ddd;
              }
              /* Horizontal separation lines */
              .dashboard-container .charts-flex-row:not(:last-child) .chart-card::before {
                content: "";
                position: absolute;
                bottom: 0;
                left: 0;
                width: 100%;
                height: 1px;
                background: #ddd;
              }
              @media (max-width: 1200px) {
                .dashboard-container .charts-flex-row {
                  flex-direction: column;
                  min-width: unset;
                }
                .dashboard-container .chart-card:not(:last-child)::after {
                  display: none;
                }
                .dashboard-container .charts-flex-row:not(:last-child) .chart-card::before {
                  display: none;
                }
              }
              
              /* Only target dropdowns within the dashboard component */
              .dashboard-container .custom-dropdown .dropdown-item.active, 
              .dashboard-container .custom-dropdown .dropdown-item:active {
                background-color: #5A6ACF !important;
                color: white !important;
              }
              .dashboard-container .custom-dropdown .dropdown-item:hover {
                background-color: #f8f9fa !important;
                color: #5A6ACF !important;
              }

              /* Top Searched Prompts Bullet Styles */
              .top-searched-prompts-list {
                padding-left: 20px;
                margin-bottom: 10px;
                max-width: 100%;
                overflow: hidden;
              }
              .top-searched-prompts-list li {
                margin-bottom: 8px;
                word-break: break-word;
                line-height: 1.4;
                max-width: 100%;
                overflow: hidden;
              }
              .last-updated-caption {
                font-size: 12px;
                color: #666;
                font-style: italic;
                text-align: center;
                margin-top: 15px;
                padding-top: 10px;
                border-top: 1px solid #eee;
                max-width: 100%;
                overflow: hidden;
              }

              /* Chart container fixes for zoom */
              .apexcharts-canvas {
                max-width: 100% !important;
                overflow: hidden !important;
              }
              
              .apexcharts-inner {
                max-width: 100% !important;
                transform-origin: center center !important;
              }
              
              .apexcharts-svg {
                max-width: 100% !important;
                overflow: hidden !important;
              }

              /* Fix for top searched prompts dropdown to open upwards */
              .top-prompts-dropup .dropdown-menu {
                bottom: 100%;
                top: auto !important;
              }

              /* NEW: Shift all website dropdowns and top prompts dropdown to the left */
              .dashboard-container .custom-dropdown .dropdown-menu,
              .dashboard-container .top-prompts-dropup .dropdown-menu {
                transform: translateX(-10px) !important;
                margin-left: -10px !important;
                margin-top: 30px !important; 
              }

              /* Ensure proper positioning for dropup */
              .dashboard-container .top-prompts-dropup .dropdown-menu {
                bottom: 100% !important;
                top: auto !important;
              }

              /* Mobile adjustments for the shifted dropdowns */
              @media (max-width: 768px) {
                .dashboard-container .custom-dropdown .dropdown-menu,
                .dashboard-container .top-prompts-dropup .dropdown-menu {
                  transform: translateX(-5px) !important;
                  margin-left: -5px !important;
                }
              }
            `}
          </style>
          
          <Row>
            <Col md="12" lg="12">
              <Row className="row-cols-1">
                <div className="overflow-hidden" data-aos="fade-up" data-aos-delay="800">
                  {/* Stats Container - Replaced Swiper with HTML design */}
                  <div className="stats-container">
                    {loadingStats ? (
                      <SectionLoader label="Loading stats..." className="revize-section-loader--compact" />
                    ) : (() => {
                      const boxes = [];

                      // Total User box (for ADMIN and SITEADMIN)
                      if (userRole === "ADMIN" || userRole === "SITEADMIN") {
                        boxes.push({
                          icon: <i className="fas fa-users"></i>,
                          label: "Users",
                          value: userCount,
                          link: null
                        });
                      }

                      // Total Client/Website box
                      boxes.push({
                        icon: userRole === "ADMIN" ? <i className="fas fa-user-check"></i> : <i className="fas fa-desktop"></i>,
                        label: userRole === "ADMIN" ? "Client" : "Website",
                        value: siteCount,
                        link: {
                            to: "/site",
                          }
                      });

                      // Total Trained Documents box
                      const trainedDocumentsBox = {
                        icon: <i className="fas fa-desktop"></i>,
                        label: "Trained Documents",
                        value: trainedDocCount,
                        link: userRole !== "ADMIN" ? {
                          to: "/site/docs",
                          state: { siteId: siteId, siteName: siteName, siteKey: siteKey }
                        } : null
                      };
                      boxes.push(trainedDocumentsBox);

                      // Total FST Pending Records box (for ADMIN only)
                      if (userRole === "ADMIN") {
                        boxes.push({
                          icon: <i className="fas fa-file-alt"></i>,
                          label: "FST Pending Records",
                          value: fstData,
                          link: {
                            to: "/site/pending_fst",
                            state: { siteId: siteId, siteName: siteName, siteKey: siteKey }
                          }
                        });
                      }

                      return boxes.map((box, index) => {
                        const boxContent = (
                          <>
                            <div className="icon">{box.icon}</div>
                            <div className="stat-info">
                              <div className="stat-label">{box.label}</div>
                              <div className="stat-value">
                                <CountUp start={0} end={box.value} duration={3} />
                              </div>
                              {box.growth && (
                                <div className="growth">
                                  <i className="fas fa-arrow-up"></i> {box.growth}
                                </div>
                              )}
                            </div>
                          </>
                        );

                        return box.link ? (
                          <Link
                            key={index}
                            to={box.link.to}
                            state={box.link.state}
                            style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '15px' }}
                          >
                            {boxContent}
                          </Link>
                        ) : (
                          <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            {boxContent}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </Row>
            </Col>

            {(userRole === "ADMIN" || userRole === "SITEADMIN") &&
              (<>
                {/* Dashboard Section with White Background */}
                <div className="dashboard-section" style={{
                  backgroundColor: '#FDFDFE',
                  padding: '20px',
                  margin: '20px 40px',
                  borderRadius: '20px',
                  width: 'calc(100% - 80px)',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
                  maxWidth: '100%',
                  overflow: 'hidden'
                }}>
                  {/* Dashboard Title */}
                  <h4 style={{
                    textAlign: 'left',
                    marginBottom: '20px',
                    color: '#333',
                    fontWeight: '400'
                  }}>
                    Dashboard
                  </h4>

                  {/* Charts Flex Container with separation lines */}
                  <div className="charts-flex-container">
                    {/* First Row: Word Counts and Prompt Counts */}
                    <div className="charts-flex-row">
                      {/* Word Counts Card */}
                      <div className="chart-card">
                        <Row className="align-items-center">
                          <Col xs={12} md={6}>
                            <h5>Word Counts</h5>
                          </Col>
                          <Col xs={12} md={6} className="text-md-end mt-2 mt-md-0">
                            <Dropdown onSelect={(key) => setTimePeriod(key)} className="d-inline mx-1 mb-1 mb-md-0 custom-dropdown">
                              <Dropdown.Toggle
                                variant="outline-primary"
                                size="sm"
                                style={customDropdownStyles.dropdownToggle}
                              >
                                {timePeriod}
                              </Dropdown.Toggle>
                              <Dropdown.Menu style={{ ...customDropdownStyles.dropdownMenu, maxHeight: "200px", overflowY: "auto" }}>
                                <Dropdown.Item eventKey="Daily" active={timePeriod === "Daily"}>Daily</Dropdown.Item>
                                <Dropdown.Item eventKey="Weekly" active={timePeriod === "Weekly"}>Weekly</Dropdown.Item>
                                <Dropdown.Item eventKey="Monthly" active={timePeriod === "Monthly"}>Monthly</Dropdown.Item>
                                <Dropdown.Item eventKey="Yearly" active={timePeriod === "Yearly"}>Yearly</Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>

                            <Dropdown onSelect={(key) => setSelectedWebsite(key)} className="d-inline mx-1 custom-dropdown">
                              <Dropdown.Toggle
                                variant="outline-secondary"
                                size="sm"
                                style={customDropdownStyles.dropdownToggle}
                              >
                                {selectedWebsite}
                              </Dropdown.Toggle>
                              <Dropdown.Menu style={{ ...customDropdownStyles.dropdownMenu, maxHeight: "200px", overflowY: "auto" }}>
                                <Dropdown.Item eventKey="All" active={selectedWebsite === "All"}>All</Dropdown.Item>
                                {websiteNameList.map((name, index) => (
                                  <Dropdown.Item key={index} eventKey={name} active={selectedWebsite === name}>
                                    {name}
                                  </Dropdown.Item>
                                ))}
                              </Dropdown.Menu>
                            </Dropdown>
                          </Col>
                        </Row>
                        <div style={{ marginTop: "20px", maxWidth: "100%", overflow: "hidden", minHeight: 320 }}>
                          {loadingWordCount ? (
                            <SectionLoader label="Loading word counts..." />
                          ) : (
                            <ApexCharts options={chartOptions} series={chartSeries} type="line" height={320} />
                          )}
                        </div>
                      </div>

                      {/* Prompt Counts Card */}
                      <div className="chart-card">
                        <Row className="align-items-center">
                          <Col xs={12} md={6}>
                            <h5>Prompt Counts</h5>
                          </Col>
                          <Col xs={12} md={6} className="text-md-end mt-2 mt-md-0">
                            <Dropdown onSelect={(key) => setDateFilterForPromptCount(key)} className="d-inline mx-1 mb-1 mb-md-0 custom-dropdown">
                              <Dropdown.Toggle
                                variant="outline-primary"
                                size="sm"
                                style={customDropdownStyles.dropdownToggle}
                              >
                                {dateFilterForPromptCount}
                              </Dropdown.Toggle>
                              <Dropdown.Menu style={{ ...customDropdownStyles.dropdownMenu, maxHeight: "200px", overflowY: "auto" }}>
                                <Dropdown.Item eventKey="Daily" active={dateFilterForPromptCount === "Daily"}>Daily</Dropdown.Item>
                                <Dropdown.Item eventKey="Weekly" active={dateFilterForPromptCount === "Weekly"}>Weekly</Dropdown.Item>
                                <Dropdown.Item eventKey="Monthly" active={dateFilterForPromptCount === "Monthly"}>Monthly</Dropdown.Item>
                                <Dropdown.Item eventKey="Yearly" active={dateFilterForPromptCount === "Yearly"}>Yearly</Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>

                            <Dropdown onSelect={(key) => setSelectedWebsiteForPromptCount(key)} className="d-inline mx-1 custom-dropdown">
                              <Dropdown.Toggle
                                variant="outline-secondary"
                                size="sm"
                                style={customDropdownStyles.dropdownToggle}
                              >
                                {selectedWebsiteForPromptCount}
                              </Dropdown.Toggle>
                              <Dropdown.Menu style={{ ...customDropdownStyles.dropdownMenu, maxHeight: "200px", overflowY: "auto" }}>
                                <Dropdown.Item eventKey="All" active={selectedWebsiteForPromptCount === "All"}>All</Dropdown.Item>
                                {websiteNameList.map((name, index) => (
                                  <Dropdown.Item key={index} eventKey={name} active={selectedWebsiteForPromptCount === name}>
                                    {name}
                                  </Dropdown.Item>
                                ))}
                              </Dropdown.Menu>
                            </Dropdown>
                          </Col>
                        </Row>
                        <div style={{ marginTop: "20px", maxWidth: "100%", overflow: "hidden", minHeight: 320 }}>
                          {loadingPromptCount ? (
                            <SectionLoader label="Loading prompt counts..." />
                          ) : (
                            <ApexCharts options={chartOptionsForPromptCount} series={chartSeriesForPromptCount} type="bar" height={320} />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Second Row: User Analytics and Reaction Analytics */}
                    <div className="charts-flex-row">
                      {/* User Analytics Card */}
                      <div className="chart-card">
                        <Row className="align-items-center">
                          <Col xs={12} md={6}>
                            <h5>User Analytics</h5>
                            <p style={{ fontSize: "14px", color: "#555", marginTop: "5px" }}>
                              Click a bar to view region-wise analytics
                            </p>
                          </Col>
                          <Col xs={12} md={6} className="text-md-end mt-2 mt-md-0">
                            <Dropdown onSelect={(key) => setDateFilterForRegionalAnalytics(key)} className="d-inline mx-1 mb-1 mb-md-0 custom-dropdown">
                              <Dropdown.Toggle
                                variant="outline-primary"
                                size="sm"
                                style={customDropdownStyles.dropdownToggle}
                              >
                                {dateFilterForRegionalAnalytics}
                              </Dropdown.Toggle>
                              <Dropdown.Menu style={{ ...customDropdownStyles.dropdownMenu, maxHeight: "200px", overflowY: "auto" }}>
                                <Dropdown.Item eventKey="Daily" active={dateFilterForRegionalAnalytics === "Daily"}>Daily</Dropdown.Item>
                                <Dropdown.Item eventKey="Weekly" active={dateFilterForRegionalAnalytics === "Weekly"}>Weekly</Dropdown.Item>
                                <Dropdown.Item eventKey="Monthly" active={dateFilterForRegionalAnalytics === "Monthly"}>Monthly</Dropdown.Item>
                                <Dropdown.Item eventKey="Yearly" active={dateFilterForRegionalAnalytics === "Yearly"}>Yearly</Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>

                            <Dropdown onSelect={(key) => setSelectedWebsiteForUserAnalytics(key)} className="d-inline mx-1 custom-dropdown">
                              <Dropdown.Toggle
                                variant="outline-secondary"
                                size="sm"
                                style={customDropdownStyles.dropdownToggle}
                              >
                                {selectedWebsiteForUserAnalytics}
                              </Dropdown.Toggle>
                              <Dropdown.Menu style={{ ...customDropdownStyles.dropdownMenu, maxHeight: "200px", overflowY: "auto" }}>
                                <Dropdown.Item eventKey="All" active={selectedWebsiteForUserAnalytics === "All"}>All</Dropdown.Item>
                                {websiteNameList.map((name, index) => (
                                  <Dropdown.Item key={index} eventKey={name} active={selectedWebsiteForUserAnalytics === name}>
                                    {name}
                                  </Dropdown.Item>
                                ))}
                              </Dropdown.Menu>
                            </Dropdown>
                          </Col>
                        </Row>
                        <div style={{ marginTop: "20px", maxWidth: "100%", overflow: "hidden", minHeight: 320 }}>
                          {loadingUserAnalytics ? (
                            <SectionLoader label="Loading user analytics..." />
                          ) : (
                            <ApexCharts options={regionalAnalyticsChartOptions} series={regionalAnalyticsChartSeries} type="bar" height={320} />
                          )}
                        </div>
                      </div>

                      {/* Reaction Analytics Card */}
                      <div className="chart-card">
                        <Row className="align-items-center">
                          <Col xs={12} md={6}>
                            <h5>Reaction Analytics</h5>
                          </Col>
                          <Col xs={12} md={6} className="text-md-end mt-2 mt-md-0">
                            <Dropdown onSelect={(key) => setDateFilterForReactionCount(key)} className="d-inline mx-1 mb-1 mb-md-0 custom-dropdown">
                              <Dropdown.Toggle
                                variant="outline-primary"
                                size="sm"
                                style={customDropdownStyles.dropdownToggle}
                              >
                                {dateFilterForReactionCount}
                              </Dropdown.Toggle>
                              <Dropdown.Menu style={{ ...customDropdownStyles.dropdownMenu, maxHeight: "200px", overflowY: "auto" }}>
                                <Dropdown.Item eventKey="Daily" active={dateFilterForReactionCount === "Daily"}>Daily</Dropdown.Item>
                                <Dropdown.Item eventKey="Weekly" active={dateFilterForReactionCount === "Weekly"}>Weekly</Dropdown.Item>
                                <Dropdown.Item eventKey="Monthly" active={dateFilterForReactionCount === "Monthly"}>Monthly</Dropdown.Item>
                                <Dropdown.Item eventKey="Yearly" active={dateFilterForReactionCount === "Yearly"}>Yearly</Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>

                            <Dropdown onSelect={(key) => setSelectedWebsiteForReactionCount(key)} className="d-inline mx-1 custom-dropdown">
                              <Dropdown.Toggle
                                variant="outline-secondary"
                                size="sm"
                                style={customDropdownStyles.dropdownToggle}
                              >
                                {selectedWebsiteForReactionCount}
                              </Dropdown.Toggle>
                              <Dropdown.Menu style={{ ...customDropdownStyles.dropdownMenu, maxHeight: "200px", overflowY: "auto" }}>
                                <Dropdown.Item eventKey="All" active={selectedWebsiteForReactionCount === "All"}>All</Dropdown.Item>
                                {websiteNameList.map((name, index) => (
                                  <Dropdown.Item key={index} eventKey={name} active={selectedWebsiteForReactionCount === name}>
                                    {name}
                                  </Dropdown.Item>
                                ))}
                              </Dropdown.Menu>
                            </Dropdown>
                          </Col>
                        </Row>
                        <div style={{ marginTop: "60px", maxWidth: "100%", overflow: "hidden", minHeight: 320 }}>
                          {loadingReactionCount ? (
                            <SectionLoader label="Loading reaction analytics..." />
                          ) : (
                            <ApexCharts
                              options={reactionChartOptions}
                              series={reactionChartSeries}
                              type="line"
                              height={320}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Top Searched Prompts Card */}
                <Card className="top-searched-prompts-card" style={{
                  width: "calc(100% - 80px)",
                  padding: "20px",
                  margin: "20px 40px",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
                  borderRadius: "20px",
                  border: "none",
                  maxWidth: "100%",
                  overflow: "visible"
                }}>
                  <Row className="align-items-center">
                    <Col xs={12} md={6}>
                      <h5>Top Searched Prompts</h5>
                    </Col>
                    <Col xs={12} md={6} className="text-md-end mt-2 mt-md-0">
                      <Dropdown 
                        drop="up"
                        onSelect={(key) => setSelectedWebsiteForTopSearchedPrompts(key)} 
                        className="d-inline mx-1 custom-dropdown top-prompts-dropup"
                      >
                        <Dropdown.Toggle
                          variant="outline-secondary"
                          size="sm"
                          style={customDropdownStyles.dropdownToggle}
                        >
                          {selectedWebsiteForTopSearchedPrompts}
                        </Dropdown.Toggle>
                        <Dropdown.Menu style={{ 
                          ...customDropdownStyles.dropdownMenu, 
                          maxHeight: "200px", 
                          overflowY: "auto"
                        }}>
                          {websiteNameList.map((name, index) => (
                            <Dropdown.Item key={index} eventKey={name} active={selectedWebsiteForTopSearchedPrompts === name}>
                              {name}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>

                      <Dropdown
                        drop="up"
                        onSelect={(key) => setSelectedTopPromptLimit(Number(key))}
                        className="d-inline mx-1 custom-dropdown top-prompts-dropup"
                      >
                        <Dropdown.Toggle
                          variant="outline-secondary"
                          size="sm"
                          style={customDropdownStyles.dropdownToggle}
                        >
                          Top {selectedTopPromptLimit}
                        </Dropdown.Toggle>
                        <Dropdown.Menu style={{ 
                          ...customDropdownStyles.dropdownMenu, 
                          maxHeight: "200px", 
                          overflowY: "auto"
                        }}>
                          {[5, 10, 15, 20].map((limit) => (
                            <Dropdown.Item
                              key={limit}
                              eventKey={limit}
                              active={selectedTopPromptLimit === limit}
                            >
                              Top {limit}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    </Col>
                  </Row>

                  <div className="top-searched-prompts-content" style={{ marginTop: "20px" }}>
                    {fetchingPrompts ? (
                      <SectionLoader label="Loading top searched prompts..." className="revize-section-loader--compact" />
                    ) : (
                      topSearchedPrompts.prompts && topSearchedPrompts.prompts.length > 0 ? (
                        <div>
                          <ul className="top-searched-prompts-list">
                            {topSearchedPrompts.prompts.map((prompt, index) => (
                              <li key={index}>
                                {prompt}
                              </li>
                            ))}
                          </ul>
                          {/* Always show the last updated caption, even if timestamp is null */}
                          <div className="last-updated-caption">
                            {formatLastUpdated(topSearchedPrompts.last_updated)}
                          </div>
                        </div>
                      ) : (
                        selectedWebsiteForTopSearchedPrompts === "Select Website" ? 
                          <p>Select website to view the top searched prompts</p> : 
                          <p>No prompts available for the selected website.</p>
                      )
                    )}
                  </div>
                </Card>
              </>
              )}

            {/* Modal for detailed view */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
              <Modal.Header closeButton>
                <Modal.Title>
                  User Analytics : {new Date(selectedDate).toLocaleDateString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: '2-digit'
                  }).replace(/\//g, '-')}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {selectedDateData && (
                  <div className="table-responsive">
                    <table className="table table-striped table-bordered" style={{ width: '100%' }}>
                      <thead>
                        <tr>
                          <th style={{ width: '70%', textAlign: 'left' }}>Region</th>
                          <th style={{ width: '30%', textAlign: 'center' }}>User Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedDateData.map((item, index) => (
                          <tr key={index}>
                            <td style={{ textAlign: 'left' }}>{item.timezone}</td>
                            <td style={{ textAlign: 'center' }}>{item.activeUsers}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Modal.Body>
              <Modal.Footer>
                <div style={{ marginRight: 'auto' }}>
                  <strong>
                    {(() => {
                      if (!selectedDateData) return { regionCount: 0, userCount: 0 };
                      
                      const regionCount = selectedDateData.length;
                      const userCount = selectedDateData.reduce((total, item) => total + item.activeUsers, 0);
                      
                      return `${regionCount} region${regionCount !== 1 ? 's' : ''} with ${userCount} user${userCount !== 1 ? 's' : ''}`;
                    })()}
                  </strong>
                </div>
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  Close
                </Button>
              </Modal.Footer>
            </Modal>

          </Row>
        </div>
      )}
    </Fragment>
  );
})

export default Index